import { keepPreviousData } from "@tanstack/react-query";
import { ShareBar } from "PFApp/components/activity_share/share_bar";
import {
  FiltersV2Button,
  FiltersV2Panel,
  FiltersV2Preview,
  FiltersV2StoreContextProvider,
  useApiFilters,
  useFiltersV2Actions,
  useFiltersV2MetaAutoUpdate,
  useOnFiltersUpdate
} from "PFApp/components/filters_v2";
import { ProfileAvatarConnected } from "PFApp/components/profile_avatar_connected";
import { RolesTogglesControls } from "PFApp/components/roles/roles_toggles_controls";
import SortBy from "PFApp/components/sort_by";
import { MatchingRelationType } from "PFApp/constants/matching_relation_type";
import { TemplateKey } from "PFApp/constants/templates";
import { AvailabilityContent, AvailabilityContext } from "PFApp/profiles/common/availability";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import useIsAvailabilityPermitted from "PFApp/use_is_availability_permitted";
import { Inline } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import PageTitle from "PFComponents/page/page_title";
import { SimplePagination } from "PFComponents/pagination/simple_pagination";
import { getProfileName } from "PFCore/helpers/profile";
import { useProfileMatchingRoles } from "PFCore/hooks/queries/profile/use_profile_matching_roles";
import { KnownFiltersV2 } from "PFCore/types/filters_v2";
import { Id, Profile } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRolesStatistics } from "../../marketplace/hooks/use_roles_statistics";
import { PageSection } from "../show/parts/page_section";
import BookingList from "./booking_list";
import { useProfileMatchingRolesQueryParams } from "./hooks/use_profile_matching_roles_query_params";
import { MatchingRole } from "./matching_role";
import css from "./profile_matching_roles_page.module.scss";

type MatchingRolesPageProps = {
  profile: Profile;
};

const MatchingRolesPageContent = ({ profile }: MatchingRolesPageProps) => {
  const { t } = useTranslation("profiles");

  const isAvailabilityPermitted = useIsAvailabilityPermitted(profile);
  const { showGrowl } = useGrowlStoreActions();

  const { apiFilters } = useApiFilters();
  const { resetValues, toggleFiltersSidePanel } = useFiltersV2Actions();

  const [rolesToShare, setRolesToShare] = useState<Id[]>([]);
  const [requirementsOn, setRequirementsOn] = useState(true);
  const [availabilityOn, setAvailabilityOn] = useState(true);

  const { updateOrder, updatePage, updateRelation, relationType, order, ...params } =
    useProfileMatchingRolesQueryParams();

  const {
    data: matchingRoles,
    isLoading,
    isError: isMatchingRolesError,
    errorUpdatedAt: matchingRolesErrorUpdatedAt
  } = useProfileMatchingRoles(
    { profileId: profile.id, order, relationType, filters: apiFilters, ...params },
    {
      placeholderData: keepPreviousData
    }
  );

  useEffect(() => {
    if (isMatchingRolesError) {
      showGrowl({ message: t("matchingRoles.errorRetrievingMatches"), kind: "error" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingRolesErrorUpdatedAt]);

  const rolesStatistics = useRolesStatistics(matchingRoles?.entries, ["shares"]);

  const meta = useMemo(
    () => matchingRoles?.meta ?? { total: 0, page: 0, perPage: 0, totalPages: 0 },
    [matchingRoles?.meta]
  );

  useFiltersV2MetaAutoUpdate(meta);

  useOnFiltersUpdate(() => updatePage(1));

  const onInvite = useCallback(() => {
    showGrowl({
      message: t("matchingRoles.roleAssignedSuccess", { name: getProfileName(profile) }),
      kind: "success"
    });
  }, [showGrowl, profile, t]);

  const handleRelationTypeChange = useCallback(
    (relation: MatchingRelationType) => {
      updatePage(1);
      resetValues();
      toggleFiltersSidePanel(false);
      updateRelation(relation);
    },
    [resetValues, toggleFiltersSidePanel, updatePage, updateRelation]
  );

  const handleShare = (id: Id) =>
    rolesToShare.includes(id)
      ? setRolesToShare(rolesToShare.filter((roleId) => roleId !== id))
      : setRolesToShare(rolesToShare.concat(id));

  return (
    <>
      <ShareBar
        style={{}}
        activityIds={rolesToShare as number[]}
        onCancel={() => setRolesToShare([])}
        templateKey={TemplateKey.Role}
      />
      {profile && isAvailabilityPermitted && (
        <PageSection
          title={
            <div className={css.summary}>
              <ProfileAvatarConnected profile={profile} size={50} />
              <h3 className={css.summaryName}>{getProfileName(profile)}</h3>
            </div>
          }
        >
          {/* @ts-ignore */}
          <AvailabilityContext profile={profile} isAdmin={false}>
            <div className={css.availabilitySection}>
              <div className={css.availabilityCalendar}>
                {/* @ts-ignore */}
                <AvailabilityContent activeViews={["calendar"]} profile={profile} readOnly={true} />
              </div>
              <div className={css.bookingsContainer}>
                <BookingList />
              </div>
            </div>
          </AvailabilityContext>
        </PageSection>
      )}
      <div className={isLoading ? css.loading : ""}>
        <PageSection className={css.section} contentStyle={{ overflow: "visible" }}>
          <PageTitle
            actions={
              <Inline alignItems="center" justifyContent="end">
                <RolesTogglesControls
                  relationType={relationType}
                  onRelationTypeChange={handleRelationTypeChange}
                  disabled={isLoading}
                  requirementsOn={requirementsOn}
                  setRequirementsOn={setRequirementsOn}
                  availabilityOn={availabilityOn}
                  setAvailabilityOn={setAvailabilityOn}
                />
              </Inline>
            }
          >
            {t("matchingRoles.title")}
          </PageTitle>
          <Inline justifyContent="space-between">
            <Inline alignItems="center" className={css.filtersPreviewWrapper}>
              {meta.order && (
                <SortBy
                  className={css.orderSelector}
                  options={meta.order.options.map((option) => ({
                    item: option,
                    displayElement: option.text,
                    id: option.text
                  }))}
                  onSortChange={updateOrder}
                  sort={meta.order.value?.text}
                />
              )}
              <FiltersV2Preview omit={[]} />
            </Inline>
            <FiltersV2Button />
          </Inline>
          <FiltersV2Panel savedFiltersProps={{ omit: { main: [KnownFiltersV2.Id] } }} />
          {!isLoading && matchingRoles?.entries.length === 0 && t("matchingRoles.noMatchingRoles")}
          {matchingRoles?.entries?.length === 0 && isLoading && (
            <LoadingDots circlesEnabled className={css.loadingDots} />
          )}
          {matchingRoles?.entries.map((match) => (
            <MatchingRole
              key={match?.activity?.id}
              profile={profile}
              match={match}
              statistics={rolesStatistics?.[match?.activity?.id]}
              onInvite={onInvite}
              handleShare={handleShare}
              rolesToShare={rolesToShare}
            />
          ))}
        </PageSection>
      </div>
      {meta.totalPages > 1 && (
        <SimplePagination
          currentPage={params.page}
          handleClick={updatePage}
          totalPages={meta.totalPages}
          disabled={isLoading}
        />
      )}
      <div className={css.space}></div>
    </>
  );
};

export const MatchingRolesPage = (props: MatchingRolesPageProps) => (
  <FiltersV2StoreContextProvider collection={PageTarget.MarketplaceHome} persistent={false}>
    <MatchingRolesPageContent {...props} />
  </FiltersV2StoreContextProvider>
);
